import { packageExtras } from './format.js';
import { metres } from './util.js';
import { WORLD_NOT_LESSON_TEST } from './not-a-lesson.js';
import { orderedFeatures, orderedStates } from './order.js';
/**
 * World design rules (ADR-069; decided by Pieter 16 Oct 2026, after the content maker's world "Op
 * Mars": its own rover drove a route 11–35 m out, the library also had a model "marsrover", and a
 * generated lesson started the world's rover instead of the model). One place for the numbers, the
 * word list and a short Dutch text per rule, so the kit, the platform's upload, the generator and a
 * later help page in the console say the same. Applied to a package when it is uploaded (`wereld
 * check`, the platform's upload), never to one that is served: published worlds keep working.
 */
/**
 * The lesson area: where the platform offers library models their spots, lanes and rings. Derived
 * from model-spots.ts and model-rings.ts, not guessed (design-rules-area.test.ts works it out again):
 * on the open stage (nothing to push a spot out), with code prompt v8's close-up, the farthest a
 * model's body reaches over the spots (lane ends and run starts among them) and rings offered to one
 * lesson's models together. Each model keeps apart from the spots of those before it, so a later one
 * is pushed out: the T. rex after the six other fixture models got its lane end at 35 m and reached
 * 40.2 m (alone it reaches 26.4 m; three T. rexes, the most a design picks, 38.3 m). Since ADR-079 the
 * still objects among them stand beside the pupil first and push it out no longer: the farthest is the
 * three T. rexes' 38.3 m. The area is kept at 40.5 m (a world-kit rule; shrinking it is Pieter's call,
 * and it is no less safe). Beyond it a spot
 * is pushed out only by the world's own scenery, and a route's lane counts as scenery (scenery.ts), so
 * a model is never offered a place on a road even there: the area is about what a lesson must plan
 * round, not about collisions.
 */
export const LESSON_AREA = {
    /** Metres from the middle of the stage. */
    radius: 40.5,
    /** Metres more that a route keeps (its vehicle's half-size, a little air). */
    margin: 2,
};
/**
 * Common library subjects (nl and en): a moving part named so warns in `wereld check`, which cannot
 * know the library. The platform compares with the library itself and refuses a clear match.
 */
export const SUBJECT_WORDS = [
    'rover',
    'marsrover',
    'trex',
    'tyrannosaurus',
    'dinosaurus',
    'dinosaur',
    'dino',
    'triceratops',
    'haai',
    'shark',
    'auto',
    'raket',
    'rocket',
    'locomotief',
    'stoomlocomotief',
    'locomotive',
    'trein',
    'train',
    'ruimteschip',
    'spaceship',
    'sterrenkruiser',
    'helikopter',
    'helicopter',
    'vliegtuig',
    'airplane',
    'drone',
    'robot',
    'walvis',
    'whale',
    'dolfijn',
    'dolphin',
    'paard',
    'horse',
    'olifant',
    'elephant',
];
const AREA = metres(LESSON_AREA.radius);
const OUT = metres(LESSON_AREA.radius + LESSON_AREA.margin);
/** How a maker marks a feature as the world's own subject, in wereld.json and in the kit. */
export const SUBJECT_OPTION = '"onderwerp": true bij het onderdeel; in de kit kit.feature(naam, onderdeel, { onderwerp: true })';
/** The rules as a maker or a teacher reads them (Dutch), with their numbers. */
export const WORLD_DESIGN_RULES = [
    {
        id: 'achtergrond',
        titel: 'Achtergrond blijft achtergrond',
        uitleg: `Wat door de wereld rijdt, vaart of vliegt (een route), blijft buiten het lesgebied: de ${AREA} m rond het podium waar het platform modellen een plek, een route en een rondje geeft, plus ${metres(LESSON_AREA.margin)} m speling. Elk punt van de route ligt dus verder dan ${OUT} m van het midden. Zo hoeft een les nooit om iets heen te plannen dat beweegt en waarvan ze de weg niet kent. Dat geldt niet voor het onderwerp van de wereld (zie daar).`,
        weigert: true,
        lesgebied: LESSON_AREA.radius,
        speling: LESSON_AREA.margin,
    },
    {
        id: 'geen-dubbel-onderwerp',
        titel: 'Geen dubbel onderwerp',
        uitleg: 'Wat in de wereld beweegt, is niet hetzelfde soort ding als een model uit de bibliotheek (een rover, een T. rex, een haai, een auto). Anders kiest een les het verkeerde: de wereld-rover in plaats van het model. Lijkt de sleutel of de naam van een bewegend onderdeel op de naam van een model uit de bibliotheek, dan weigert het platform de wereld: maak er een model van, of laat het staan. Lijkt alleen de naam van zijn route of mesh erop ("rijdend-rover"), of is het het onderwerp van de wereld, dan waarschuwt het platform alleen.',
        weigert: true,
        woorden: SUBJECT_WORDS,
    },
    {
        id: 'bewegen-op-de-plek',
        titel: 'Bewegen op de plek mag',
        uitleg: 'Wat op zijn plek beweegt of verandert, mag overal buiten de vrije ruimte: de rook van een vulkaan, de wieken van een molen, het getij, wuivend gras en bomen, een zwerm vlinders boven zijn eigen veld, een lamp die aangaat, een onderdeel dat van toestand wisselt zonder te verplaatsen.',
        weigert: false,
    },
    {
        id: 'onderwerp-van-de-wereld',
        titel: 'Onderwerp van de wereld',
        uitleg: `Gaat de wereld over wat er beweegt (de bloedstroom, een race van zaadcellen), markeer dat onderdeel dan als het onderwerp van de wereld: ${SUBJECT_OPTION}. Zijn routes mogen dan in het lesgebied komen (nooit in de vrije ruimte rond het podium), het hoeft geen model uit de bibliotheek te zijn, en een les mag erom draaien. Lijkt zijn naam op een model uit de bibliotheek, dan waarschuwt het platform: een les kan dan beide kiezen.`,
        weigert: false,
    },
    {
        id: 'de-plek-niet-de-les',
        titel: 'De plek, niet de les',
        uitleg: `${WORLD_NOT_LESSON_TEST} Het platform weigert een wereld met tekst of aanwijzers (een mesh, blok of onderdeel dat naar tekst, namen, labels, pijlen of aanwijzers heet; een onderdeel dat die aan- en uitzet, of dat alleen borden aan- en uitzet als een set namen) en een pakket met een les erin. Het waarschuwt bij een onderdeel dat een bord aanzet of een mesh die naar een bord heet, bij toestanden die lezen als de stappen van een les (stap, vraag, uitleg, genummerd, "na-de-…", vijf toestanden die elk meer laten zien), bij een beschrijving die uitlegt of vragen stelt, en bij een wereld over iets dat de leerling van dichtbij onderzoekt (een oog, een cel, een molecuul); wie de wereld goedkeurt, beoordeelt die waarschuwingen. Een proces mag: de klep in een ader die sluit of een vulkaan die uitbarst, is decor.`,
        weigert: true,
    },
];
/**
 * The routes the code worlds had themselves, which a package of their slug keeps (its lessons use
 * them): only the modern street's four lanes, by id and path (within `CODE_ROUTE_SLACK` m a point), as
 * the code drew them and the published `modern-street` and the kit's `moderne-straat` have them.
 */
export const CODE_WORLD_ROUTES = {
    'modern-street': [
        {
            id: 'auto-oost',
            path: [
                [-120, -16.8],
                [120, -16.8],
            ],
        },
        {
            id: 'auto-west',
            path: [
                [120, -20.2],
                [-120, -20.2],
            ],
        },
        {
            id: 'fiets-oost',
            path: [
                [-120, -13.75],
                [120, -13.75],
            ],
        },
        {
            id: 'fiets-west',
            path: [
                [120, -23.25],
                [-120, -23.25],
            ],
        },
    ],
};
export const CODE_ROUTE_SLACK = 0.5;
/** Whether a package's route is one its code world had (`CODE_WORLD_ROUTES`). */
function codeWorldRoute(slug, id, path) {
    const own = Object.hasOwn(CODE_WORLD_ROUTES, slug) ? CODE_WORLD_ROUTES[slug] : [];
    return own.some((route) => route.id === id &&
        route.path.length === path.length &&
        route.path.every(([x, z], i) => Math.hypot(x - path[i][0], z - path[i][1]) <= CODE_ROUTE_SLACK + 1e-9));
}
/** The nearest point of a polyline (closed when asked) to the origin. */
function nearestOnPath(points, closed) {
    let best = { distance: Infinity, at: points[0] };
    const last = closed ? points.length : points.length - 1;
    for (let i = 0; i < Math.max(1, last); i++) {
        const [xi, zi] = points[i];
        const [xj, zj] = points[(i + 1) % points.length];
        const ex = xj - xi;
        const ez = zj - zi;
        const t = Math.min(1, Math.max(0, -(xi * ex + zi * ez) / Math.max(1e-12, ex * ex + ez * ez)));
        const at = [xi + ex * t, zi + ez * t];
        const distance = Math.hypot(at[0], at[1]);
        if (distance < best.distance)
            best = { distance, at };
    }
    return best;
}
/** The routes and swarms of a package, with the features that drive them. */
export function movingParts(pkg) {
    const drivers = (id) => Object.entries(pkg.features)
        .filter(([, feature]) => Object.values(feature.states).some((changes) => Object.keys(changes).some((key) => key.startsWith(`${id}.`))))
        .map(([name]) => name);
    const subject = (features) => features.length > 0 && features.every((name) => pkg.features[name]?.onderwerp === true);
    const parts = [];
    pkg.blocks.forEach((block, index) => {
        if (block.type === 'route') {
            const features = drivers(block.id);
            parts.push({
                block: index,
                type: 'route',
                id: block.id,
                part: block.part,
                features,
                subject: subject(features),
                nearest: nearestOnPath(block.path, block.closed),
                farthest: Math.max(...block.path.map(([x, z]) => Math.hypot(x, z))),
                kept: codeWorldRoute(pkg.slug, block.id, block.path),
            });
        }
        else if (block.type === 'zwerm') {
            const features = drivers(block.id);
            parts.push({
                block: index,
                type: 'zwerm',
                id: block.id,
                part: block.kind,
                features,
                subject: subject(features),
                kept: false,
            });
        }
    });
    return parts;
}
const round1 = (n) => Math.round(n * 10) / 10 + 0;
/**
 * "Achtergrond blijft achtergrond": every point of every route of a staged world farther from the
 * middle than the lesson area and its margin, but the routes its code world had (`CODE_WORLD_ROUTES`)
 * and the routes of the world's own subject (`onderwerp`; the clear space still keeps them out,
 * stage.ts). A route a feature drives is told it may be marked so.
 */
export function lessonAreaProblems(pkg) {
    if (!packageExtras(pkg).staged)
        return [];
    const out = LESSON_AREA.radius + LESSON_AREA.margin;
    const problems = [];
    for (const part of movingParts(pkg)) {
        if (part.type !== 'route' || part.nearest === undefined || part.kept || part.subject)
            continue;
        if (part.nearest.distance >= out - 1e-9)
            continue;
        const [x, z] = part.nearest.at;
        problems.push({
            path: `/blocks/${part.block}/path`,
            message: `de route "${part.id}" (${part.part}) komt op ${metres(round1(part.nearest.distance))} m van het midden, bij [${metres(round1(x))}, ${metres(round1(z))}]: wat door de wereld rijdt, vaart of vliegt, blijft buiten het lesgebied (${AREA} m, met ${metres(LESSON_AREA.margin)} m speling: elk punt verder dan ${OUT} m). ${part.features.length > 0
                ? `Leg de route verder weg, laat het ding op zijn plek staan, of maak het onderdeel het onderwerp van de wereld als de wereld erover gaat (${SUBJECT_OPTION})`
                : 'Leg de route verder weg, of laat het ding op zijn plek staan'}`,
        });
    }
    return problems;
}
/**
 * Where the world's own subject comes into the lesson area (allowed, 20 Oct 2026): one note per subject
 * feature, its routes' nearest, for the upload's task ("Let op: …") and the item page, so the platform
 * knows a lesson plans round it.
 */
export function subjectAreaNotes(pkg) {
    if (!packageExtras(pkg).staged)
        return [];
    const out = LESSON_AREA.radius + LESSON_AREA.margin;
    const nearest = new Map();
    for (const part of movingParts(pkg)) {
        if (part.type !== 'route' || part.nearest === undefined || part.kept || !part.subject)
            continue;
        if (part.nearest.distance >= out - 1e-9)
            continue;
        for (const feature of part.features) {
            const was = nearest.get(feature);
            if (was === undefined || part.nearest.distance < was)
                nearest.set(feature, part.nearest.distance);
        }
    }
    return orderedFeatures(pkg)
        .filter((feature) => nearest.has(feature))
        .map((feature) => ({
        path: `/features/${feature}`,
        message: `"${pkg.features[feature].name}" is het onderwerp van de wereld; zijn route komt op ${metres(round1(nearest.get(feature)))} m van het midden`,
    }));
}
export const STOP_WORDS = new Set([
    'de',
    'het',
    'een',
    'van',
    'met',
    'en',
    'op',
    'in',
    'the',
    'and',
    'with',
    'of',
    'on',
]);
/** A name's words: lower case, accents off, split on anything but letters and digits, no articles. */
function nameParts(text) {
    return text
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .split(/[^a-z0-9]+/)
        .filter((part) => part !== '' && !STOP_WORDS.has(part));
}
/**
 * The words a name is compared by: its words (`nameParts`) of three letters or more, and the whole
 * joined ("T. rex" → rex, trex).
 */
export function subjectTokens(text) {
    const parts = nameParts(text);
    const words = new Set(parts.filter((part) => part.length >= 3));
    const joined = parts.join('');
    if (joined.length >= 3)
        words.add(joined);
    return [...words];
}
/**
 * What a library model is called by: of its slug and each name, the last word (the head: "Grote witte
 * haai" → haai) and the whole joined (grotewittehaai, marsrover). Never its search words: "zee",
 * "vis" or "museum" say what it is about, not what it is.
 */
export function librarySubjectTokens(model) {
    const words = new Set();
    for (const text of [model.slug, model.name, model.nameEn ?? '']) {
        const parts = nameParts(text);
        if (parts.length === 0)
            continue;
        words.add(parts.at(-1));
        words.add(parts.join(''));
    }
    return [...words];
}
/** Words shorter than this name nothing (zee, vis, car): too many things are called so. */
export const SUBJECT_MIN = 4;
/**
 * Whether a moving part's word names a library model's word -- one matching for the platform's
 * refusal, the kit's warning and the generator's check: equal, of at least four letters; or the
 * model's word a compound whose last part is the world's word, after at least three letters (a world
 * "rover" and the model "Marsrover"; "haai" and "Grote witte haai"). Never the other way round: a
 * world "zeerover" or "speelgoedauto" is not the model "Rover" or "Auto", and "automaat" no "auto".
 */
export function sameSubjectWord(world, library) {
    if (world.length < SUBJECT_MIN)
        return false;
    if (world === library)
        return true;
    return library.length - world.length >= 3 && library.endsWith(world);
}
/** A moving part's own words: its block's id and mesh (or kind). */
const blockWords = (part) => [
    ...new Set([part.id, part.part].flatMap(subjectTokens)),
];
/** A feature's words: its key and its name. */
const ownWords = (pkg, feature) => [
    ...new Set([feature, pkg.features[feature]?.name ?? ''].flatMap(subjectTokens)),
];
/**
 * A moving part's words: its block's id and mesh (or kind), and the keys and names of the features
 * driving it.
 */
function partWords(pkg, part) {
    return [
        ...new Set([...blockWords(part), ...part.features.flatMap((name) => ownWords(pkg, name))]),
    ];
}
/** Whether a feature is the world's own subject. */
const isSubject = (pkg, feature) => pkg.features[feature]?.onderwerp === true;
/**
 * The most severe match of a moving part by `named` (its words to what they name): a feature that is
 * not the world's subject, by its key or name (a refusal); else a subject feature; else the block's id
 * or mesh only. Judged per feature, so a subject feature driving the same route frees no other.
 */
function matchPart(pkg, part, named) {
    for (const subject of [false, true]) {
        for (const feature of part.features) {
            if (isSubject(pkg, feature) !== subject)
                continue;
            const found = named(ownWords(pkg, feature));
            if (found !== undefined)
                return { ...found, by: 'onderdeel', feature, subject };
        }
    }
    const found = named(blockWords(part));
    return found === undefined ? undefined : { ...found, by: 'route', subject: false };
}
/**
 * Each moving part (route or swarm) whose word names a library model (`sameSubjectWord`, against
 * `librarySubjectTokens`), its most severe match (`matchPart`). A code world's own routes are kept.
 */
export function subjectMatches(pkg, library) {
    const models = library.map((model) => ({ model, words: librarySubjectTokens(model) }));
    const first = (words) => {
        for (const m of models) {
            const word = words.find((w) => m.words.some((own) => sameSubjectWord(w, own)));
            if (word !== undefined)
                return { model: m.model, word };
        }
        return undefined;
    };
    const matches = [];
    for (const part of movingParts(pkg)) {
        if (part.kept)
            continue;
        const found = matchPart(pkg, part, first);
        if (found !== undefined)
            matches.push({ part, ...found });
    }
    return matches;
}
/** Whether the platform refuses a match: by a feature's own key or name, not the world's subject. */
const refuses = (match) => match.by === 'onderdeel' && !match.subject;
/** The feature a match names ("De rover"), else the part's first, else its block's id. */
function matchName(pkg, part, feature) {
    const name = feature ?? part.features[0];
    return name !== undefined ? (pkg.features[name]?.name ?? part.id) : part.id;
}
/** Where a match is named: its feature, else the part's first feature, else its block. */
function matchPath(part, feature) {
    const name = feature ?? part.features[0];
    return name !== undefined ? `/features/${name}` : `/blocks/${part.block}`;
}
/**
 * "Geen dubbel onderwerp" on the platform: a moving part with a driving feature, not the world's own
 * subject, whose key or name names a library model (`subjectMatches`). One problem per moving part; a
 * code world's own routes are kept.
 */
export function subjectClashes(pkg, library) {
    return subjectMatches(pkg, library)
        .filter(refuses)
        .map(({ part, model, feature }) => ({
        path: matchPath(part, feature),
        message: `Het bewegende onderdeel "${matchName(pkg, part, feature)}" lijkt op het model ${model.name} uit de bibliotheek: maak er een model van, of laat het staan. Gaat de wereld over dit onderdeel zelf, maak het dan het onderwerp van de wereld (${SUBJECT_OPTION}); dan waarschuwt het platform alleen`,
    }));
}
/**
 * The matches the platform only warns of (20 Oct 2026): by a route's or swarm's id or mesh only
 * ("rijdend-rover" beside the Marsrover), or a feature that is the world's own subject.
 */
export function subjectLookalikes(pkg, library) {
    return subjectMatches(pkg, library)
        .filter((match) => !refuses(match))
        .map(({ part, model, word, by, feature }) => by === 'route'
        ? {
            path: `/blocks/${part.block}`,
            message: `De ${part.type === 'route' ? 'route' : 'zwerm'} "${part.id}" (${part.part})${part.features.length > 0 ? ` van "${matchName(pkg, part)}"` : ''} draagt de naam van het model ${model.name} uit de bibliotheek ("${word}"): een les kiest het model, niet dit onderdeel. Is het iets anders, geef de ${part.type === 'route' ? 'route en haar mesh' : 'zwerm'} dan een eigen naam`,
        }
        : {
            path: matchPath(part, feature),
            message: `"${matchName(pkg, part, feature)}" is het onderwerp van de wereld en lijkt op het model ${model.name} uit de bibliotheek ("${word}"): een les kan het onderdeel of het model kiezen. Is het iets anders, geef het dan een eigen naam`,
        });
}
/**
 * "Geen dubbel onderwerp" in the kit, which cannot know the library: a moving part named by
 * `SUBJECT_WORDS`, said as the platform would judge it (a refusal or a warning).
 */
export function subjectWarnings(pkg) {
    const known = SUBJECT_WORDS;
    const named = (words) => {
        const word = words.find((w) => known.some((k) => sameSubjectWord(w, k)));
        return word === undefined ? undefined : { word };
    };
    const warnings = [];
    for (const part of movingParts(pkg)) {
        if (part.kept)
            continue;
        const match = matchPart(pkg, part, named);
        if (match === undefined)
            continue;
        const name = matchName(pkg, part, match.feature);
        warnings.push({
            path: matchPath(part, match.feature),
            message: refuses(match)
                ? `Het bewegende onderdeel "${name}" lijkt op een model uit de bibliotheek ("${match.word}"): heeft de bibliotheek zo'n model, dan weigert het platform de wereld (een les zou dit onderdeel kiezen in plaats van het model). Maak er een model van, of laat het staan; gaat de wereld over dit onderdeel zelf, maak het dan het onderwerp van de wereld (${SUBJECT_OPTION})`
                : `Het bewegende onderdeel "${name}" lijkt op een model uit de bibliotheek ("${match.word}"): heeft de bibliotheek zo'n model, dan waarschuwt het platform (${match.subject ? 'het is het onderwerp van de wereld' : 'alleen de naam van de route of mesh lijkt erop'}). Is het iets anders, geef het dan een eigen naam`,
        });
    }
    return warnings;
}
/**
 * The package's features that drive a route, in the package's order; a feature driving only routes
 * its code world had (`CODE_WORLD_ROUTES`) is none (the modern street's traffic, its lessons' own).
 */
export function movingFeatures(pkg) {
    const routes = movingParts(pkg).filter((part) => part.type === 'route' && !part.kept);
    return orderedFeatures(pkg).flatMap((feature) => {
        const { name, states } = pkg.features[feature];
        const driven = routes.filter((part) => part.features.includes(feature));
        if (driven.length === 0)
            return [];
        const nearest = driven.reduce((a, b) => (b.nearest.distance < a.nearest.distance ? b : a));
        const [x, z] = nearest.nearest.at;
        const runs = (part, changes) => {
            const block = pkg.blocks[part.block];
            if (block?.type !== 'route')
                return false;
            const set = changes[`${part.id}.level`];
            const level = typeof set === 'number' ? set : block.level;
            const out = block.present[0] + (block.present[1] - block.present[0]) * level;
            return out > 0 && Math.max(block.speed[0], block.speed[1]) > 0;
        };
        return [
            {
                feature,
                name,
                words: [...new Set(driven.flatMap((part) => partWords(pkg, part)))],
                nearest: round1(nearest.nearest.distance),
                farthest: round1(Math.max(...driven.map((part) => part.farthest))),
                azimuth: Math.round((Math.atan2(x, -z) * 180) / Math.PI) + 0,
                movingStates: orderedStates(pkg, feature).filter((state) => driven.some((part) => runs(part, states[state]))),
                ...(pkg.features[feature].onderwerp === true ? { onderwerp: true } : {}),
            },
        ];
    });
}
//# sourceMappingURL=design-rules.js.map